import { useCallback, useEffect, useState, type JSX, type ReactNode } from 'react'
import {
  ApiError,
  api,
  can,
  saveFile,
  type ManagementReport,
  type ReportDoc,
  type ReportQuery,
} from '../lib/api'
import { ErrorBox, ListCount, Loading } from '../components/DataTable'
import { FilterBar, type FilterGroup, type FilterValue } from '../components/FilterBar'
import { VendorPicker } from '../components/VendorPicker'
import { Breakdowns, BREAKDOWNS_QUESTION } from '../components/report/Breakdowns'
import { usePeopleNames } from '../lib/people'
import { cellWords } from '../lib/reportWords'
import '../styles/lists.css'

// Section 18's eight reports, and the breakdowns the Programme overview used
// to carry.
//
// WHY THIS SCREEN KNOWS NOTHING ABOUT ANY OF THEM
//
// It renders a column list and rows of text, and the API decides what those
// are. Eight hand-built screens would be eight places for a column to be
// renamed on one side and not the other, and the failure would look like a
// working report with a heading that no longer describes what is under it.
// The filters are drawn the same way: the API says which ones a report takes
// and what values each one accepts, so this file offers exactly what the
// report will honour and nothing it would refuse.
//
// WHY IT LOOKS THE WAY IT DOES
//
// The product officer's review of 17 September 2026 (observation 3.17) found
// the chips, chart and tabs hard to read. So each tab says the one question it
// answers under the tab row, the filters are the one Filters control every
// register uses with the vendor chosen by name beside it, and the count is at
// the foot of the rows like every other list.
//
// WHY THE CSV IS A FETCH AND NOT A LINK
//
// The API needs the bearer token, and a browser sends a plain anchor without
// one. So the file is fetched, held as a blob and handed to a temporary anchor,
// which is what the evidence pack download already does.

// A stored enum reaching a screen, and only that.
//
// The shape is deliberately narrow: capitals with at least one underscore.
// A PAN has no underscore, a vendor code has a hyphen, and a date has digits
// and dashes, so none of them is touched. Only the values that are codes in
// the database and words to a person.
//
// The CSV is built by the server and is not affected: the file still carries
// INSUFFICIENT_EVIDENCE, because a file is read by another program.


const NUMERIC = new Set(['NUMBER'])

// The tab that is not a report from the catalogue but the Programme overview's
// breakdowns (observation 3.18).
const BREAKDOWNS = 'breakdowns'

// The API offers filter values in its own vocabulary. A person reads words.
function filterWord(v: string): string {
  return v.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase())
}

export function Reports(): JSX.Element {
  const [catalogue, setCatalogue] = useState<ReportDoc[] | null>(null)
  const [catalogueError, setCatalogueError] = useState<unknown>(null)

  const [chosen, setChosen] = useState<string>('')
  const [picked, setPicked] = useState<FilterValue>({})
  const [vendorId, setVendorId] = useState('')

  const [report, setReport] = useState<ManagementReport | null>(null)
  const [running, setRunning] = useState(false)
  const [runError, setRunError] = useState<unknown>(null)
  const [saving, setSaving] = useState(false)
  // The risk register in the plan of action format auditors read. It lived on
  // the Risk screen's header; it is here because an auditor's copy is a report,
  // and the Risk screen keeps only the matrix and the register.
  const [auditSaving, setAuditSaving] = useState(false)
  const [auditError, setAuditError] = useState<unknown>(null)

  useEffect(() => {
    api
      .reports()
      .then((r) => {
        setCatalogue(r.reports)
        const first = r.reports[0]
        if (first) setChosen(first.key)
      })
      .catch(setCatalogueError)
  }, [])

  const onBreakdowns = chosen === BREAKDOWNS
  const doc = onBreakdowns ? null : (catalogue?.find((d) => d.key === chosen) ?? null)

  // Run on arrival and whenever the question changes, so the answer is on the
  // screen rather than one press away.
  useEffect(() => {
    if (!doc) return
    const t = window.setTimeout(() => run(), 60)
    return () => window.clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chosen, picked, vendorId, catalogue])

  // The filters belong to the report that declared them. Carrying a severity
  // from the remediation report onto the vendor master would be refused by the
  // API, which is the right answer and a poor thing to make somebody discover.
  const choose = useCallback((key: string) => {
    setChosen(key)
    setPicked({})
    setVendorId('')
    setRunError(null)
  }, [])

  function query(): ReportQuery {
    const q: ReportQuery = {}
    if (vendorId.trim()) q.vendorId = vendorId.trim()
    for (const [name, values] of Object.entries(picked)) {
      if (!values.length) continue
      if (name === 'status') q.status = values
      if (name === 'band') q.band = values
      if (name === 'tier') q.tier = values
      if (name === 'severity') q.severity = values
      if (name === 'kind') q.kind = values
    }
    return q
  }

  function run(): void {
    if (!doc) return
    setRunning(true)
    setRunError(null)
    api
      .report(doc.key, query())
      .then((r) => setReport(r.report))
      .catch((err) => {
        setReport(null)
        setRunError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err)))
      })
      .finally(() => setRunning(false))
  }

  // The same rows in the format the reader picked: a CSV for other tools, or
  // an Excel workbook (GET /v1/reports/{key}?format=xlsx) for people.
  function download(format: 'csv' | 'xlsx'): void {
    if (!doc) return
    setSaving(true)
    setRunError(null)
    ;(format === 'xlsx' ? api.reportXlsx(doc.key, query()) : api.reportCsv(doc.key, query()))
      .then(saveFile)
      .catch((err) => setRunError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))))
      .finally(() => setSaving(false))
  }

  function downloadForAuditors(): void {
    setAuditSaving(true)
    setAuditError(null)
    api
      .riskRegisterPOAM()
      .then(saveFile)
      .catch((err) => setAuditError(err instanceof ApiError ? err : new ApiError(0, 'UNKNOWN', String(err))))
      .finally(() => setAuditSaving(false))
  }

  const question = onBreakdowns ? BREAKDOWNS_QUESTION : doc?.question

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Reports</h1>
        </div>
        {/* The board pack is its own page, reached from Home's Generate report;
            a reader whose job has no Home (an executive) starts here. */}
        <div className="chiprow">
          {can('audit.read') && (
            <button type="button" data-action="tprm.reports.a03" onClick={downloadForAuditors} disabled={auditSaving}>
              {auditSaving ? 'Preparing' : 'Risk register for auditors'}
            </button>
          )}
          <a className="button" href="#/report/programme?period=30d">
            Programme board report
          </a>
          <a className="button" href="#/report/dates-to-check">
            Dates to check
          </a>
        </div>
      </div>

      {auditError != null && <ErrorBox error={auditError} what="The risk register for auditors" verb="downloaded" />}

      {catalogueError != null && <ErrorBox error={catalogueError} what="The report catalogue" />}
      {!catalogue && !catalogueError && <Loading what="the reports" />}

      {catalogue && (
        <>
          <div className="report-tabs" role="tablist" aria-label="Reports">
            {catalogue.map((d) => (
              <button
                type="button"
                role="tab"
                key={d.key}
                className={'report-tab' + (d.key === chosen ? ' on' : '')}
                aria-selected={d.key === chosen}
                onClick={() => choose(d.key)}
              >
                {d.title}
              </button>
            ))}
            <button
              type="button"
              role="tab"
              className={'report-tab' + (onBreakdowns ? ' on' : '')}
              aria-selected={onBreakdowns}
              onClick={() => choose(BREAKDOWNS)}
            >
              Breakdowns
            </button>
          </div>

          {question && <p className="report-question">{question}</p>}

          {onBreakdowns && <Breakdowns />}

          {doc && (
            <section className="panel report-panel">
              <ReportControls
                doc={doc}
                picked={picked}
                onPicked={setPicked}
                vendorId={vendorId}
                onVendorId={setVendorId}
              >
                <button type="button" onClick={run} disabled={running}>
                  {running ? 'Running' : 'Refresh'}
                </button>
                <button type="button" data-action="tprm.reports.a02" onClick={() => download('csv')} disabled={saving}>
                  Download CSV
                </button>
                <button
                  type="button"
                  className="primary"
                  data-action="tprm.reports.a01"
                  onClick={() => download('xlsx')}
                  disabled={saving}
                >
                  {saving ? 'Preparing' : 'Download Excel'}
                </button>
              </ReportControls>

              {runError != null && <ErrorBox error={runError} what={doc.title} verb="run" />}

              {report && report.key === doc.key && (
                <>
                  <ReportNumbers report={report} />
                  <ReportTable report={report} />
                </>
              )}
              {running && !report && <p className="muted">Reading the register.</p>}
            </section>
          )}
        </>
      )}
    </>
  )
}

// The report's filters in one row: the Filters control every register uses,
// the vendor chosen by name, and the report's own buttons at the end.
function ReportControls({
  doc,
  picked,
  onPicked,
  vendorId,
  onVendorId,
  children,
}: {
  doc: ReportDoc
  picked: FilterValue
  onPicked: (v: FilterValue) => void
  vendorId: string
  onVendorId: (v: string) => void
  children: ReactNode
}): JSX.Element {
  const groups: FilterGroup[] = doc.filters
    .filter((f) => f.name !== 'vendorId' && (f.values ?? []).length > 0)
    .map((f) => ({
      key: f.name,
      label: f.label,
      options: (f.values ?? []).map((v) => ({ value: v, label: filterWord(v) })),
    }))
  const vendorFilter = doc.filters.find((f) => f.name === 'vendorId')
  const tail = (
    <>
      {vendorFilter && (
        <VendorPicker
          id={'rf-' + doc.key + '-vendor'}
          label={vendorFilter.label}
          compact
          value={vendorId}
          onChange={onVendorId}
          placeholder="One vendor: search by name"
        />
      )}
      {children}
    </>
  )
  if (groups.length === 0) {
    return (
      <div className="filterbar">
        <div className="filterbar-row">
          <span className="filterbar-spacer" />
          {tail}
        </div>
      </div>
    )
  }
  return (
    <FilterBar groups={groups} value={picked} onChange={onPicked}>
      {tail}
    </FilterBar>
  )
}

// Which column a reader would group by if they were doing it in a
// spreadsheet: the first one whose values repeat and are few.
function groupingColumn(report: ManagementReport): number {
  const preferred = ['band', 'severity', 'status', 'tier', 'kind', 'risktype', 'result', 'state']
  const byName = report.columns.findIndex((c) => preferred.includes(c.key.toLowerCase()))
  if (byName >= 0) return byName
  for (let i = 0; i < report.columns.length; i++) {
    if (NUMERIC.has(report.columns[i]?.kind ?? 'TEXT')) continue
    const values = new Set(report.rows.map((r) => r[i] ?? ''))
    if (values.size > 1 && values.size <= 6 && report.rows.length > values.size) return i
  }
  return -1
}

const TONE: Record<string, string> = {
  critical: 'critical',
  high: 'high',
  medium: 'medium',
  low: 'low',
  overdue: 'critical',
  open: 'high',
  active: 'low',
}

// The answer in numbers, by the column a reader would group on. The count of
// rows is not here: it is at the foot of the table, where every list keeps it.
function ReportNumbers({ report }: { report: ManagementReport }): JSX.Element | null {
  const gi = groupingColumn(report)
  const counts = new Map<string, number>()
  if (gi >= 0) {
    for (const row of report.rows) {
      const v = cellWords((row[gi] ?? '').trim(), report.columns[gi]?.key) || 'Not recorded'
      counts.set(v, (counts.get(v) ?? 0) + 1)
    }
  }
  const ordered = [...counts.entries()].sort((a, b) => b[1] - a[1])
  // One group is no breakdown: a single tile saying every row is the same
  // thing repeats the table under it.
  if (ordered.length < 2) return null
  const most = ordered[0]?.[1] ?? 1
  return (
    <div className="report-numbers">
      <div className="report-kpis">
        {ordered.slice(0, 4).map(([v, n]) => (
          <div className={'report-kpi tone-' + (TONE[v.toLowerCase()] ?? 'plain')} key={v}>
            <span className="n">{n}</span>
            <span className="l">{v}</span>
          </div>
        ))}
      </div>
      {ordered.length > 1 && (
        <div className="report-bars" aria-label={'By ' + (report.columns[gi]?.label ?? 'group')}>
          {ordered.map(([v, n]) => (
            <div className="report-bar" key={v}>
              <span className="k">{v}</span>
              <span className={'b tone-' + (TONE[v.toLowerCase()] ?? 'plain')}>
                <i style={{ width: Math.max(3, (n / most) * 100) + '%' }} />
              </span>
              <span className="v">{n}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

function ReportTable({ report }: { report: ManagementReport }): JSX.Element {
  // An owner column holds subjects; the reader wants the person.
  const names = usePeopleNames()
  return (
    <>
      {report.total === 0 && (
        <p className="report-count" role="status">
          Nothing matched. <span className="muted">Read against {report.asOf}.</span>
        </p>
      )}
      {report.rows.length > 0 && (
        <>
          <div className="table-wrap">
            <table className="grid report-grid">
              <thead>
                <tr>
                  {report.columns.map((c) => (
                    <th key={c.key} className={NUMERIC.has(c.kind) ? 'num' : undefined} scope="col">
                      <span className="th-static">{c.label}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j} className={NUMERIC.has(report.columns[j]?.kind ?? 'TEXT') ? 'num' : undefined}>
                        {cell === '' ? <span className="muted">not recorded</span> : (names[cell] ?? cellWords(cell, report.columns[j]?.key))}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* The rows on this page against what the filter matched. A report
              that stopped at its limit says so here rather than describing a
              smaller estate than the one that exists. */}
          <ListCount shown={report.rows.length} total={report.total} noun="rows">
            {report.omitted > 0 ? ', narrow the filters for the rest' : ''}
          </ListCount>
        </>
      )}
    </>
  )
}
